import { Chip as HeroChip, type ChipRootProps } from "@heroui/react";
import React from "react";

type V3Variant = "primary" | "secondary" | "tertiary" | "soft";
type V2Variant =
  | "solid"
  | "bordered"
  | "light"
  | "flat"
  | "faded"
  | "shadow"
  | "dot";

type V3Color = "accent" | "danger" | "default" | "success" | "warning";
type V2Color =
  | "default"
  | "primary"
  | "secondary"
  | "success"
  | "warning"
  | "danger";

export interface AppChipProps
  extends Omit<ChipRootProps<"span">, "variant" | "color"> {
  variant?: V3Variant | V2Variant;
  color?: V3Color | V2Color;
  /** v2 classNames - ignored in v3 wrapper. */
  classNames?: { base?: string; content?: string; [k: string]: unknown };
  /** v2 startContent - rendered inline. */
  startContent?: React.ReactNode;
  /** v2 endContent - rendered inline. */
  endContent?: React.ReactNode;
  /** v2 radius - ignored. */
  radius?: "none" | "sm" | "md" | "lg" | "full";
  /** v2 onClose - renders close button via children when desired. */
  onClose?: () => void;
  /** v2 onClick - passes through to root span. */
  onClick?: React.MouseEventHandler<HTMLSpanElement>;
  /** v2 avatar - rendered before children. */
  avatar?: React.ReactNode;
  /** v2 isClosable - ignored. */
  isClosable?: boolean;
  /** v2 isDisabled - applies aria-disabled style. */
  isDisabled?: boolean;
  /** v2 style - passes through. */
  style?: React.CSSProperties;
}

function mapVariant(v: AppChipProps["variant"]): V3Variant {
  switch (v) {
    case "primary":
    case "secondary":
    case "tertiary":
    case "soft":
      return v;
    case "flat":
    case "light":
    case "faded":
      return "soft";
    case "dot":
      return "primary";
    case "bordered":
      return "tertiary";
    default:
      return "soft";
  }
}

function mapColor(c: AppChipProps["color"]): V3Color {
  switch (c) {
    case "accent":
    case "danger":
    case "default":
    case "success":
    case "warning":
      return c;
    case "primary":
      return "accent";
    case "secondary":
      return "default";
    default:
      return "default";
  }
}

export const Chip = ({
  variant,
  color,
  classNames,
  startContent,
  endContent,
  avatar,
  radius: _radius,
  onClose: _onClose,
  onClick,
  isClosable: _isClosable,
  isDisabled,
  style,
  children,
  className,
  ...rest
}: AppChipProps) => {
  const v3Variant = mapVariant(variant);
  const v3Color = mapColor(color);
  const baseClass = classNames?.base;
  const interactiveClass = onClick ? "cursor-pointer" : "";
  const disabledClass = isDisabled ? "opacity-50 pointer-events-none" : "";
  const finalClass = [baseClass, interactiveClass, disabledClass, className]
    .filter(Boolean)
    .join(" ");

  const hasLeading = avatar || startContent;
  const inner =
    hasLeading || endContent ? (
      <span
        className={[
          // Keep leading/trailing adornments (icons, avatars) on one line and
          // vertically centred with the label - a bare span left the icon
          // misaligned and cramped against the text.
          "inline-flex items-center gap-1",
          classNames?.content as string | undefined,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        {avatar}
        {startContent}
        {children}
        {endContent}
      </span>
    ) : classNames?.content ? (
      <span className={classNames.content as string}>{children}</span>
    ) : (
      children
    );

  return (
    <HeroChip
      {...(rest as ChipRootProps<"span">)}
      className={finalClass || undefined}
      color={v3Color}
      style={style}
      variant={v3Variant}
      onClick={onClick}
    >
      {inner}
    </HeroChip>
  );
};
